import { getUserAndTokenFromStorage, setUser } from '@firsttable/functions';
import parseErrorMessage from '@discover-lib/parseErrorMessage';
import Logger from '@/lib/logger';
import makeRequest from './makeRequest';
import RefreshTokenMutation from '../mutations/refreshToken';

const LOG_ENABLED = false;
const logger = new Logger('REFRESH_USER_TOKEN', 'LOG');
if (LOG_ENABLED) {
  logger.enable();
}

/**
 * Refreshes the user's authentication token using the refreshToken mutation.
 * This is called when an UNAUTHORIZED error is received.
 */
export default async function refreshUserToken(
  requestId: string,
  headers: Headers,
  abortSignal?: AbortSignal
): Promise<void> {
  const { user } = getUserAndTokenFromStorage();

  if (!user?.id) {
    if (LOG_ENABLED) {
      logger.log(`${requestId} - No user to refresh`);
    }
    throw new Error('No user to refresh');
  }

  if (LOG_ENABLED) {
    logger.log(`${requestId} - Starting token refresh`, { userId: user.id });
  }

  try {
    // Call the refresh mutation using makeRequest
    const { errors, data } = await makeRequest({
      requestId,
      query: RefreshTokenMutation,
      variables: {},
      headers,
      abortSignal,
    });

    if (errors && errors.length > 0) {
      if (LOG_ENABLED) {
        logger.log(`${requestId} - Refresh mutation returned errors`, errors);
      }
      throw new Error(errors.map((e) => e.message).join('\n'));
    }

    const newToken = data.refreshToken?.token;
    const newCdnToken = data.refreshToken?.cdnToken;

    if (!newToken) {
      if (LOG_ENABLED) {
        logger.log(`${requestId} - No token returned from refresh`, data.refreshToken);
      }
      throw new Error('No token returned from refresh');
    }

    // Update the token in local storage
    setUser({
      user: { ...user, isLoggedIn: true },
      token: newToken,
      cdnToken: newCdnToken || '',
    });

    if (LOG_ENABLED) {
      logger.log(`${requestId} - Token refreshed successfully`);
    }
  } catch (error) {
    // If refresh fails, clear the user from storage
    if (LOG_ENABLED) {
      logger.log(`${requestId} - Token refresh failed, clearing user`, {
        error: parseErrorMessage(error),
      });
    }
    setUser(null);
    throw error;
  }
}
